/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getSearchStrFromLocalStorage } from "~/components/shared-components/search/search-service";
import { QueryParams, SearchHistoryPayload, SearchResponse } from "~/components/shared-components/search/type";
import API_PATH from "~/utils/api-routes";
import { LANG } from "~/utils/constant";
import api from "~/utils/Interceptor";
import { ApiService } from "~/utils/service";

export interface ModuleName {
  id: number;
  en_name: string;
  gu_name: string;
  mdl_key: string;
}

interface SearchState {
  isLoading: boolean;
  searchResults: SearchResponse | null;
  hasMoreResults?: boolean;
  module: ModuleName[] | null;
  allModule?: SearchResponse | null;
  hasMoreAllModule?: boolean;
  isOpenToSearch: boolean;
  selectedTab: string;
  searchText?: string | null;
}

const initialState: SearchState = {
  searchResults: {
    rows: [],
    count: 0,
  },
  isLoading: false,
  hasMoreResults: true,
  allModule: {
    rows: [],
    count: 0,
  },
  hasMoreAllModule: true,
  module: null,

  isOpenToSearch: false,
  searchText: getSearchStrFromLocalStorage() || "",
  selectedTab: "All",
};

export const getSearchResults = createAsyncThunk("search/fetchResults", async (params: QueryParams) => {
  const response = await api.get(API_PATH.SEARCH_TXT, { params });
  return {
    ...response.data.data,
    pageNumber: params.pageNumber,
    moduleName: params.module_name,
  };
});

export const getSearchModule = createAsyncThunk<ModuleName[], string>("search/getSearchModule", async (lang) => {
  const apiService = new ApiService(lang || LANG.english);
  const response = await apiService.getMasterData();
  return response?.search_modules as ModuleName[];
});

export const setSearchHistory = createAsyncThunk<any, SearchHistoryPayload>("search/fetchModule", async (data) => {
  const response = await api.post(API_PATH.SEARCH_HISTORY, data);
  return response.data;
});

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    setClearSearchResults: (state) => {
      state.searchResults = {
        rows: [],
        count: 0,
      };
      state.allModule = {
        rows: [],
        count: 0,
      };
      state.isLoading = false;
    },

    setOpenToSearch: (state, action: PayloadAction<boolean>) => {
      state.isOpenToSearch = action.payload;
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedTab = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSearchResults.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSearchResults.fulfilled, (state, action: PayloadAction<SearchResponse & { pageNumber?: number; moduleName?: string | null }>) => {
        const isFirstPage = !action.payload.pageNumber || action.payload.pageNumber === 1;
        const updatedRows = action.payload.rows.map((item) => (item.mdl === "book" ? { ...item, mdl: "sad granth" } : item));
        state.searchResults = {
          rows: isFirstPage ? updatedRows : [...(state.searchResults?.rows || []), ...updatedRows],
          count: action.payload.count || 0,
        };

        state.hasMoreResults = (action.payload.count || 0) > (isFirstPage ? action.payload.rows.length : state.searchResults.rows.length);

        if (action.payload.moduleName !== "All" && action.payload.count <= 0 && action.payload.allModule && action.payload.allModule?.count > 0) {
          const updatedModuleRows = action.payload.allModule?.rows.map((item) => (item.mdl === "book" ? { ...item, mdl: "sad granth" } : item));

          state.allModule = {
            rows: isFirstPage ? updatedModuleRows || [] : [...(state.allModule?.rows || []), ...(updatedModuleRows || [])],
            count: action.payload.allModule?.count || 0,
          };

          state.hasMoreAllModule =
            (action.payload.allModule?.count || 0) > (isFirstPage ? (updatedModuleRows?.length ?? 0) : state.allModule.rows.length);
        } else {
          state.hasMoreAllModule = false;
        }
        state.isLoading = false;
      })
      .addCase(getSearchResults.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getSearchModule.fulfilled, (state, action: PayloadAction<ModuleName[]>) => {
        state.module = action.payload;
      });
  },
});

export const { setClearSearchResults, setOpenToSearch, setSearchText, setSelectedTab } = searchSlice.actions;
export default searchSlice.reducer;
